import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Table, Spinner, Alert, Card, Button, Form, Row, Col, Badge, Modal, InputGroup, Dropdown } from 'react-bootstrap';
import { useMediaQuery } from 'react-responsive';
import MobileListLayout from '../common/MobileListLayout';
import { usePageTitle } from '../context/PageTitleContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faDownload, faCopy, faPrint, faSort, faSortUp, faSortDown, faTimes } from '@fortawesome/free-solid-svg-icons';
import FilterPanel from '../common/FilterPanel';
import { Link, useNavigate } from 'react-router-dom';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
  createColumnHelper,
} from '@tanstack/react-table';
import { getCodiciSconto, deleteCodiceSconto, deleteMultipleCodiciSconto, exportCodiciScontoCSV, createCodiciScontoBulk } from '../services/api';
import ColumnOrderController from '../common/ColumnOrderController';
import ActionToolbar from '../common/ActionToolbar';
import SortableHeader from '../common/SortableHeader';
import { checklistFilterFn } from '../common/clientFilterUtils';


const allColumnsMap = {
  id: "ID",
  codice: "Codice",
  descrizione: "Descrizione",
  tipo: "Tipo",
  percentuale_sconto: "Sconto %",
  usato: "Usato",
  cliente_nome: "Cliente",
  ordine_id: "Ordine",
  data_utilizzo: "Data Utilizzo",
  valido_dal: "Valido Dal",
  valido_fino: "Valido Fino",
  origine_marketplace: "Origine",
  data_creazione: "Data Creazione",
  note: "Note"
};

const initialVisibleColumns = ['id', 'codice', 'tipo', 'percentuale_sconto', 'usato', 'valido_dal', 'valido_fino', 'origine_marketplace'];
const visibilityKey = 'table-visibility-codici-sconto';

const columnHelper = createColumnHelper();

function CodiciScontoList() {
  usePageTitle('Codici Sconto');
  const isMobile = useMediaQuery({ maxWidth: 991 });
  const [codici, setCodici] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCodiciIds, setSelectedCodiciIds] = useState([]);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkData, setBulkData] = useState({
    quantita: 10,
    tipo: 'PRIMO_ACQUISTO',
    percentuale_sconto: 15,
    prefix: 'PRIMO15',
    origine_marketplace: 'FELTRINELLI',
    valido_giorni: 365
  });
  const navigate = useNavigate();

  const deleteApiMap = { 'codici-sconto': deleteCodiceSconto };
  const deleteMultipleApiMap = { 'codici-sconto': deleteMultipleCodiciSconto };

  const handlePrint = () => {
    if (selectedCodiciIds.length === 0) {
      alert('Seleziona almeno un codice da stampare.');
      return;
    }
    const ids = selectedCodiciIds.join(',');
    navigate(`/codici-sconto/print?ids=${ids}`);
  };

  const [columnVisibility, setColumnVisibility] = useState(() => {
    const saved = localStorage.getItem(visibilityKey);
    if (saved) return JSON.parse(saved);
    return Object.keys(allColumnsMap).reduce((acc, key) => {
      acc[key] = initialVisibleColumns.includes(key);
      return acc;
    }, {});
  });

  const [sorting, setSorting] = useState(() => {
    const saved = sessionStorage.getItem('list-sorting-codici-sconto');
    return saved ? JSON.parse(saved) : [{ id: 'data_creazione', desc: true }];
  });

  const [columnFilters, setColumnFilters] = useState(() => {
    const saved = sessionStorage.getItem('list-filters-codici-sconto');
    return saved ? JSON.parse(saved) : [];
  });

  const [columnOrder, setColumnOrder] = useState(() => {
    const savedOrder = localStorage.getItem('table-column-order-codici-sconto');
    const defaultOrder = ['select', ...Object.keys(allColumnsMap)];
    if (savedOrder) {
      let parsedOrder = JSON.parse(savedOrder);
      parsedOrder = parsedOrder.filter(c => c !== 'select');
      return ['select', ...parsedOrder];
    }
    return defaultOrder;
  });

  const [columnSizing, setColumnSizing] = useState(() => {
    const saved = localStorage.getItem('table-column-sizing-codici-sconto');
    return saved ? JSON.parse(saved) : {};
  });

  useEffect(() => {
    sessionStorage.setItem('list-sorting-codici-sconto', JSON.stringify(sorting));
  }, [sorting]);

  useEffect(() => {
    sessionStorage.setItem('list-filters-codici-sconto', JSON.stringify(columnFilters));
  }, [columnFilters]);

  useEffect(() => {
    localStorage.setItem('table-column-order-codici-sconto', JSON.stringify(columnOrder));
  }, [columnOrder]);

  useEffect(() => {
    localStorage.setItem('table-column-sizing-codici-sconto', JSON.stringify(columnSizing));
  }, [columnSizing]);

  const fetchCodiciData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getCodiciSconto(0, 1000);
      setCodici(response.data.codici_sconto || []);
    } catch (err) {
      setError('Errore nel caricamento dei codici sconto.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCodiciData();
  }, [fetchCodiciData]);

  const handleDelete = async (id) => {
    if (window.confirm('Sei sicuro di voler eliminare questo codice sconto?')) {
      try {
        await deleteCodiceSconto(id);
        alert('Codice sconto eliminato con successo!');
        fetchCodiciData();
      } catch (err) {
        alert('Errore durante l\'eliminazione: ' + (err.response?.data?.detail || err.message));
      }
    }
  };

  const handleBulkDelete = async () => {
    if (selectedCodiciIds.length === 0) {
      alert('Seleziona almeno un codice da eliminare.');
      return;
    }
    if (window.confirm(`Sei sicuro di voler eliminare ${selectedCodiciIds.length} codici sconto?`)) {
      try {
        await Promise.all(selectedCodiciIds.map(id => deleteCodiceSconto(id)));
        alert(`${selectedCodiciIds.length} codici eliminati con successo!`);
        setSelectedCodiciIds([]);
        fetchCodiciData();
      } catch (err) {
        alert('Errore durante l\'eliminazione: ' + (err.response?.data?.detail || err.message));
      }
    }
  };

  const handleExportCSV = async () => {
    if (selectedCodiciIds.length === 0) {
      alert('Seleziona almeno un codice da esportare.');
      return;
    }
    try {
      const response = await exportCodiciScontoCSV(selectedCodiciIds);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `codici_sconto_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      alert('Errore durante l\'export: ' + (err.response?.data?.detail || err.message));
    }
  };

  // Helper functions for mobile view
  const formatDate = (dateString) => {
    if (!dateString) return 'N/D';
    return new Date(dateString).toLocaleDateString('it-IT');
  };

  const getTipoBadgeVariant = (tipo) => {
    switch (tipo) {
      case 'PRIMO_ACQUISTO': return 'primary';
      case 'SCONTO_PERCENTUALE': return 'success'; 
      case 'SCONTO_FISSO': return 'info';
      case 'WELCOME': return 'warning';
      case 'LOYALTY': return 'dark';
      default: return 'secondary';
    }
  };

  const renderCodiceCard = useCallback((codice) => {
    const isExpired = new Date(codice.valido_fino) < new Date();
    const isUsed = codice.usato;
    
    return (
      <Card 
        className={`mb-2 ${isUsed ? 'border-success' : isExpired ? 'border-danger' : 'border-warning'}`} 
        style={{ fontSize: '0.9rem' }}
      >
        <Card.Body>
          <div className="d-flex justify-content-between align-items-start mb-2">
            <div>
              <strong style={{ fontSize: '1rem', fontFamily: 'monospace' }}>{codice.codice}</strong>
              <Badge bg={getTipoBadgeVariant(codice.tipo)} className="ms-2">
                {codice.tipo?.replace('_', ' ')}
              </Badge>
            </div>
            <div className="text-end">
              <strong style={{ fontSize: '1.1rem', color: '#28a745' }}>
                -{codice.percentuale_sconto}%
              </strong>
              {isUsed && (
                <Badge bg="success" className="d-block mt-1">USATO</Badge>
              )}
              {!isUsed && isExpired && (
                <Badge bg="danger" className="d-block mt-1">SCADUTO</Badge>
              )}
            </div>
          </div>

          {codice.descrizione && (
            <div className="text-muted mb-2" style={{ fontSize: '0.85rem' }}>
              {codice.descrizione}
            </div>
          )}

          <Row className="mb-2">
            <Col xs={6}>
              <small className="text-muted">Valido dal:</small>
              <div>{formatDate(codice.valido_dal)}</div>
            </Col>
            <Col xs={6}>
              <small className="text-muted">Valido fino:</small>
              <div>{formatDate(codice.valido_fino)}</div>
            </Col>
          </Row>

          {(codice.cliente_nome || codice.ordine_id) && (
            <Row className="mb-2">
              <Col xs={6}>
                <small className="text-muted">Cliente:</small>
                <div>{codice.cliente_nome || 'N/D'}</div>
              </Col>
              <Col xs={6}>
                <small className="text-muted">Ordine:</small>
                <div>{codice.ordine_id ? `#${codice.ordine_id}` : 'N/D'}</div>
              </Col>
            </Row>
          )}

          {codice.origine_marketplace && (
            <div className="mb-2">
              <small className="text-muted">Origine:</small>
              <Badge bg="info" className="ms-1">{codice.origine_marketplace}</Badge>
            </div>
          )}

          {codice.data_utilizzo && (
            <div className="mb-2">
              <small className="text-muted">Utilizzato il:</small>
              <div>{formatDate(codice.data_utilizzo)}</div>
            </div>
          )}

          <div className="d-flex gap-2 mt-3">
            <Link to={`/codici-sconto/${codice.id}/edit`}>
              <Button variant="primary" size="sm">Modifica</Button>
            </Link>
            <Button
              variant="danger"
              size="sm"
              onClick={() => handleDelete(codice.id)}
            >
              Elimina
            </Button>
            {codice.codice && (
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => {
                  navigator.clipboard.writeText(codice.codice);
                  alert('Codice copiato negli appunti!');
                }}
              >
                Copia
              </Button>
            )}
          </div>
        </Card.Body>
      </Card>
    );
  }, []);

  const actionsDropdownContent = useMemo(() => (
    <>
      <Dropdown.Item onClick={handleBulkDelete} disabled={selectedCodiciIds.length === 0}>
        Elimina Selezionati ({selectedCodiciIds.length})
      </Dropdown.Item>
      <Dropdown.Item onClick={handleExportCSV} disabled={selectedCodiciIds.length === 0}>
        <FontAwesomeIcon icon={faDownload} className="me-2" />
        Esporta CSV ({selectedCodiciIds.length})
      </Dropdown.Item>
      <Dropdown.Item onClick={handlePrint} disabled={selectedCodiciIds.length === 0}>
        <FontAwesomeIcon icon={faPrint} className="me-2" />
        Stampa ({selectedCodiciIds.length})
      </Dropdown.Item>
      <Dropdown.Divider />
      <Dropdown.Item onClick={() => setShowBulkModal(true)}>
        <FontAwesomeIcon icon={faPlus} className="me-2" />
        Genera Codici in Blocco
      </Dropdown.Item>
    </>
  ), [selectedCodiciIds.length, handleBulkDelete, handleExportCSV, handlePrint]);
  
  const handleBulkGenerate = async () => {
    try {
      // Calcola le date di validità dal numero di giorni
      const valido_dal = new Date();
      const valido_fino = new Date();
      valido_fino.setDate(valido_fino.getDate() + parseInt(bulkData.valido_giorni));
      
      const result = await createCodiciScontoBulk({
        quantita: parseInt(bulkData.quantita),
        tipo: bulkData.tipo,
        percentuale_sconto: parseFloat(bulkData.percentuale_sconto),
        prefix: bulkData.prefix,
        origine_marketplace: bulkData.origine_marketplace || null,
        valido_dal: valido_dal.toISOString(),
        valido_fino: valido_fino.toISOString()
      });
      
      alert(`${result.data.length} codici sconto generati con successo!`);
      setShowBulkModal(false);
      fetchCodiciData();
    } catch (err) {
      alert('Errore durante la generazione: ' + (err.response?.data?.detail || err.message));
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert(`Codice "${text}" copiato negli appunti!`);
  };

  const getColumnValue = (codice, columnName) => {
    if (columnName === 'usato') {
      return codice.usato ? <Badge bg="danger">Usato</Badge> : <Badge bg="success">Disponibile</Badge>;
    }
    if (columnName === 'percentuale_sconto') {
      return `${codice.percentuale_sconto}%`;
    }
    if (columnName === 'codice') {
      return (
        <div className="d-flex align-items-center gap-2">
          <code>{codice.codice}</code>
          <Button size="sm" variant="outline-secondary" onClick={() => copyToClipboard(codice.codice)}>
            <FontAwesomeIcon icon={faCopy} />
          </Button>
        </div>
      );
    }
    if (columnName === 'valido_dal' || columnName === 'valido_fino' || columnName === 'data_utilizzo' || columnName === 'data_creazione') {
      return codice[columnName] ? new Date(codice[columnName]).toLocaleDateString() : 'N/D';
    }
    if (columnName === 'cliente_nome') {
      return codice.cliente ? `${codice.cliente.nome} ${codice.cliente.cognome}` : 'N/D';
    }
    return codice[columnName] !== undefined && codice[columnName] !== null ? String(codice[columnName]) : 'N/D';
  };

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'select',
        header: ({ table }) => (
          <Form.Check
            type="checkbox"
            onChange={table.getToggleAllRowsSelectedHandler()}
            checked={table.getIsAllRowsSelected()}
          />
        ),
        cell: ({ row }) => (
          <Form.Check
            type="checkbox"
            checked={row.getIsSelected()}
            onChange={row.getToggleSelectedHandler()}
          />
        ),
        size: 50,
      }),
      columnHelper.accessor('id', {
        header: 'ID',
        cell: ({ row }) => row.original.id,
        enableSorting: true,
        enableColumnFilter: true,
        meta: { filterVariant: 'text' },
      }),
      columnHelper.accessor('codice', {
        header: 'Codice',
        cell: ({ row }) => (
          <div className="d-flex align-items-center gap-2">
            <code>{row.original.codice}</code>
            <Button size="sm" variant="outline-secondary" onClick={(e) => {
              e.stopPropagation();
              copyToClipboard(row.original.codice);
            }}>
              <FontAwesomeIcon icon={faCopy} />
            </Button>
          </div>
        ),
        enableSorting: true,
        enableColumnFilter: true,
      }),
      columnHelper.accessor('descrizione', {
        header: 'Descrizione',
        cell: ({ row }) => row.original.descrizione || 'N/D',
        enableSorting: true,
        enableColumnFilter: true,
      }),
      columnHelper.accessor('tipo', {
        header: 'Tipo',
        cell: ({ row }) => row.original.tipo,
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: checklistFilterFn,
        meta: {
          filterVariant: 'select',
          filterOptions: [
            { value: 'PRIMO_ACQUISTO', label: 'Primo Acquisto' },
            { value: 'GENERICO', label: 'Generico' },
            { value: 'SPEDIZIONE_GRATUITA', label: 'Spedizione Gratuita' }
          ]
        }
      }),
      columnHelper.accessor('percentuale_sconto', {
        header: 'Sconto %',
        cell: ({ row }) => `${row.original.percentuale_sconto}%`,
        enableSorting: true,
        enableColumnFilter: true,
        meta: { filterVariant: 'text' },
      }),
      columnHelper.accessor('usato', {
        header: 'Usato',
        cell: ({ row }) => row.original.usato ? <Badge bg="danger">Usato</Badge> : <Badge bg="success">Disponibile</Badge>,
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: checklistFilterFn,
        meta: {
          filterVariant: 'select',
          filterOptions: [{ value: false, label: 'Disponibile' }, { value: true, label: 'Usato' }]
        }
      }),
      columnHelper.accessor('cliente_nome', {
        header: 'Cliente',
        cell: ({ row }) => row.original.cliente ? `${row.original.cliente.nome} ${row.original.cliente.cognome}` : 'N/D',
        enableSorting: true,
        enableColumnFilter: true,
      }),
      columnHelper.accessor('ordine_id', {
        header: 'Ordine',
        cell: ({ row }) => row.original.ordine_id || 'N/D',
        enableSorting: true,
        enableColumnFilter: true,
        meta: { filterVariant: 'text' },
      }),
      columnHelper.accessor('data_utilizzo', {
        header: 'Data Utilizzo',
        cell: ({ row }) => row.original.data_utilizzo ? new Date(row.original.data_utilizzo).toLocaleDateString() : 'N/D',
        enableSorting: true,
        enableColumnFilter: true,
        meta: { filterVariant: 'text' },
      }),
      columnHelper.accessor('valido_dal', {
        header: 'Valido Dal',
        cell: ({ row }) => row.original.valido_dal ? new Date(row.original.valido_dal).toLocaleDateString() : 'N/D',
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: (row, columnId, filterValue) => {
          if (!filterValue) return true;
          const cellValue = row.getValue(columnId);
          if (!cellValue) return false;
          const cellDate = new Date(cellValue).toISOString().split('T')[0];
          return cellDate === filterValue;
        },
      }),
      columnHelper.accessor('valido_fino', {
        header: 'Valido Fino',
        cell: ({ row }) => row.original.valido_fino ? new Date(row.original.valido_fino).toLocaleDateString() : 'N/D',
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: (row, columnId, filterValue) => {
          if (!filterValue) return true;
          const cellValue = row.getValue(columnId);
          if (!cellValue) return false;
          const cellDate = new Date(cellValue).toISOString().split('T')[0];
          return cellDate === filterValue;
        },
      }),
      columnHelper.accessor('origine_marketplace', {
        header: 'Origine',
        cell: ({ row }) => row.original.origine_marketplace || 'N/D',
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: checklistFilterFn,
        meta: {
          filterVariant: 'select',
          filterOptions: [
            { value: 'FELTRINELLI', label: 'Feltrinelli' },
            { value: 'IBS', label: 'IBS' },
            { value: 'MARKETPLACE', label: 'Marketplace' },
            { value: 'ALTRO', label: 'Altro' }
          ]
        }
      }),
      columnHelper.accessor('data_creazione', {
        header: 'Data Creazione',
        cell: ({ row }) => row.original.data_creazione ? new Date(row.original.data_creazione).toLocaleDateString() : 'N/D',
        enableSorting: true,
        enableColumnFilter: true,
        meta: { filterVariant: 'text' },
      }),
      columnHelper.accessor('note', {
        header: 'Note',
        cell: ({ row }) => row.original.note || 'N/D',
        enableSorting: true,
        enableColumnFilter: true,
      }),
    ],
    [codici]
  );

  const table = useReactTable({
    data: codici,
    columns,
    state: {
      sorting,
      columnVisibility,
      columnFilters,
      columnOrder,
      columnSizing,
      rowSelection: selectedCodiciIds.reduce((acc, id) => {
        const index = codici.findIndex(c => c.id === id);
        if (index !== -1) acc[index] = true;
        return acc;
      }, {}),
    },
    enableRowSelection: true,
    onRowSelectionChange: (updaterOrValue) => {
      const newSelection = typeof updaterOrValue === 'function'
        ? updaterOrValue(table.getState().rowSelection)
        : updaterOrValue;
      const selectedIds = Object.keys(newSelection)
        .filter(key => newSelection[key])
        .map(key => codici[parseInt(key)].id);
      setSelectedCodiciIds(selectedIds);
    },
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnFiltersChange: setColumnFilters,
    onColumnOrderChange: setColumnOrder,
    onColumnSizingChange: setColumnSizing,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    columnResizeMode: 'onChange',
  });

  if (loading) return <div className="text-center my-5"><Spinner animation="border" /></div>;
  if (error) return <Alert variant="danger">{error}</Alert>;

  return (
    <div className="container-fluid mt-4">
      {!isMobile && (
        <ActionToolbar
          selectedItemIds={selectedCodiciIds}
          entityType="codici-sconto"
          onDeleteSuccess={fetchCodiciData}
          deleteApiMap={deleteApiMap}
          deleteMultipleApiMap={deleteMultipleApiMap}
        />
      )}
      <Card>
        {!isMobile && (
          <Card.Header>
            <Row className="align-items-center">
              <Col md={6} className="d-flex justify-content-start align-items-center gap-2">
              </Col>
              <Col md={6} className="d-flex justify-content-end align-items-center gap-2">
                <Button 
                  variant="outline-success" 
                  size="sm" 
                  onClick={handlePrint}
                  disabled={selectedCodiciIds.length === 0}
                >
                  <FontAwesomeIcon icon={faPrint} /> Stampa Selezionati
                </Button>
                <Button 
                  variant="outline-primary" 
                  size="sm" 
                  onClick={handleExportCSV}
                  disabled={selectedCodiciIds.length === 0}
                >
                  <FontAwesomeIcon icon={faDownload} /> Esporta CSV
                </Button>
                <Dropdown autoClose="outside">
                  <Dropdown.Toggle variant="outline-secondary" id="dropdown-columns" size="sm">
                    Colonne
                  </Dropdown.Toggle>
                  <Dropdown.Menu className="long-dropdown-menu p-0" style={{ minWidth: '400px' }}>
                    <div className="d-flex">
                      <div className="p-2" style={{ flex: 1, borderRight: '1px solid #dee2e6' }}>
                        <ColumnOrderController
                          table={table}
                          allColumnsMap={allColumnsMap}
                        />
                      </div>
                      <div className="p-2" style={{ flex: 1 }}>
                        <h6 className="fw-bold">Mostra Colonne</h6>
                        <div className="d-flex flex-column" style={{ maxHeight: '350px', overflowY: 'auto' }}>
                          {table.getAllColumns().filter(column => column.id !== 'select').map(column => (
                            <Form.Check
                              key={column.id}
                              type="checkbox"
                              label={allColumnsMap[column.id]}
                              checked={column.getIsVisible()}
                              onChange={column.getToggleVisibilityHandler()}
                              className="dropdown-item"
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  </Dropdown.Menu>
                </Dropdown>
                <Button variant="success" onClick={() => setShowBulkModal(true)} size="sm">
                  <FontAwesomeIcon icon={faPlus} /> Genera Bulk
                </Button>
                <Button variant="primary" as={Link} to="/codici-sconto/new" size="sm">
                  <FontAwesomeIcon icon={faPlus} /> Nuovo Codice
                </Button>
              </Col>
            </Row>
          </Card.Header>
        )}
        <Card.Body>
          {/* Codice/Tipo/Usato ora si filtrano dal popover sull'intestazione colonna
              (checklist, coerente con le altre pagine); qui restano solo i due campi data,
              che Filter.js non copre (nessuna modalita' data). "Tipo" nel pannello aveva
              anche valori ('REGALO','PROMO','SCONTO_FISSO','SCONTO_PERCENTUALE') che non
              sono mai esistiti nell'enum reale: selezionarli non avrebbe mai trovato nulla. */}
          <FilterPanel
            fields={[
              { id: 'valido_dal', label: 'Valido dal', type: 'date', colSize: 2 },
              { id: 'valido_fino', label: 'Valido fino', type: 'date', colSize: 2 },
            ]}
            onApply={(filterValues) => {
              const idsGestitiDalPannello = ['valido_dal', 'valido_fino'];
              const nuovi = Object.entries(filterValues)
                .filter(([, v]) => v !== undefined && v !== '' && v !== null)
                .map(([id, value]) => ({ id, value }));
              setColumnFilters(prev => [...prev.filter(f => !idsGestitiDalPannello.includes(f.id)), ...nuovi]);
            }}
          />
          {loading && <div className="text-center"><Spinner animation="border" /></div>}
          {error && <Alert variant="danger">{error}</Alert>}
          
          {!loading && !error && (
            <>
              {isMobile ? (
                <MobileListLayout
                  items={codici}
                  renderCard={renderCodiceCard}
                  entityName="codice sconto"
                  entityNamePlural="codici sconto"
                  createPath="/codici-sconto/new"
                  createButtonText="Nuovo Codice"
                  selectedIds={selectedCodiciIds}
                  onToggleSelect={(codiceId) => {
                    const row = table.getRowModel().rows.find(r => r.original.id === codiceId);
                    if (row) row.toggleSelected();
                  }}
                  actionsDropdownContent={actionsDropdownContent}
                  showSelectAll={true}
                  onSelectAll={() => table.toggleAllRowsSelected()}
                  isAllSelected={table.getIsAllRowsSelected()}
                />
              ) : (
                <div className="table-responsive">
            <Table striped bordered hover size="sm">
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <React.Fragment key={headerGroup.id}>
                    <tr>
                      {headerGroup.headers.map((header) => (
                        <SortableHeader key={header.id} header={header} table={table} />
                      ))}
                    </tr>
                  </React.Fragment>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map((row) => (
                  <tr
                    key={row.id}
                    onClick={() => navigate(`/codici-sconto/${row.original.id}`)}
                    style={{ cursor: 'pointer' }}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        onClick={cell.column.id === 'select' ? (e) => e.stopPropagation() : undefined}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
              )}
            </>
          )}

          {codici.length === 0 && !loading && (
            <Alert variant="info" className="text-center">
              Nessun codice sconto trovato. Creane uno nuovo!
            </Alert>
          )}
        </Card.Body>
      </Card>

      {/* Modal Generazione Bulk */}
      <Modal show={showBulkModal} onHide={() => setShowBulkModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Genera Codici Sconto in Massa</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Quantità</Form.Label>
                  <Form.Control
                    type="number"
                    value={bulkData.quantita}
                    onChange={(e) => setBulkData({ ...bulkData, quantita: e.target.value })}
                    min="1"
                    max="1000"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Tipo</Form.Label>
                  <Form.Select
                    value={bulkData.tipo}
                    onChange={(e) => setBulkData({ ...bulkData, tipo: e.target.value })}
                  >
                    <option value="PRIMO_ACQUISTO">Primo Acquisto</option>
                    <option value="GENERICO">Generico</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Sconto %</Form.Label>
                  <Form.Control
                    type="number"
                    value={bulkData.percentuale_sconto}
                    onChange={(e) => setBulkData({ ...bulkData, percentuale_sconto: e.target.value })}
                    min="0"
                    max="100"
                    step="0.1"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Prefisso</Form.Label>
                  <Form.Control
                    type="text"
                    value={bulkData.prefix}
                    onChange={(e) => setBulkData({ ...bulkData, prefix: e.target.value })}
                    placeholder="PRIMO15"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Origine Marketplace</Form.Label>
                  <Form.Select
                    value={bulkData.origine_marketplace || ''}
                    onChange={(e) => setBulkData({ ...bulkData, origine_marketplace: e.target.value || null })}
                  >
                    <option value="FELTRINELLI">Feltrinelli</option>
                    <option value="IBS">IBS</option>
                    <option value="MARKETPLACE">Marketplace Generico</option>
                    <option value="">Nessuna</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Validità (giorni)</Form.Label>
                  <Form.Control
                    type="number"
                    value={bulkData.valido_giorni}
                    onChange={(e) => setBulkData({ ...bulkData, valido_giorni: e.target.value })}
                    min="1"
                    placeholder="365"
                  />
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowBulkModal(false)}>
            Annulla
          </Button>
          <Button variant="primary" onClick={handleBulkGenerate}>
            Genera {bulkData.quantita} Codici
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default CodiciScontoList;
